import type {ReviewSummary} from '../../../page-data/payloads/review-summaries'
import type {Thread} from '@github-ui/diff-lines/conversation-types'

export interface ReviewThread {
  /** Underlying thread model. */
  thread: Thread
  /** Optional anchor prefix for this thread. Required when the same thread appears under multiple reviews. */
  anchorPrefix?: string
  /**
   * IDs of comment(s) in this thread which are part of the parent review. This would be the first comment if the review
   * created a new thread, or it could be one or more subsequent comments if the review replies to existing threads.
   */
  relevantCommentIds: string[]
}

export interface ReviewWithThreads {
  summary: ReviewSummary
  /** All threads containing comments belonging to this review. Note that the same thread may appear under multiple reviews. */
  threads: ReviewThread[]
}

interface AssignThreadsToReviewsResult {
  reviews: ReviewWithThreads[]
  /** Any threads for which a review parent was not found. */
  unassignedThreads: Thread[]
}

/**
 * Given all the review summaries and threads on this PR, determine with threads belong to which reviews based on which
 * comments _in_ those threads belong to which reviews. This is a potentially many-to-many relationship; a single review
 * may contain multiple threads, and a single thread may appear until multiple reviews.
 * Any threads for which a review was not found are surfaces as `unassignedThreads`.
 */
export function assignThreadsToReviews(
  reviewSummaries: readonly ReviewSummary[],
  threads: readonly Thread[],
): AssignThreadsToReviewsResult {
  /** Map of review ID -> deduped review summary. Summaries appear once for each review they contain. */
  const summariesById = new Map(reviewSummaries.map(summary => [summary.id, summary]))

  /** Map of review -> thread -> review-relevant comment IDs */
  const threadsByReview = new Map<ReviewSummary, Map<Thread, Omit<ReviewThread, 'thread'>>>()
  const assignedThreadIds = new Set<string>()

  // Comments belong to reviews, not threads. But comments also belong to threads, so threads indirectly are tied
  // to reviews. And (for now) only comments know which review they belong to - threads don't have data on comments.
  // Also, a single review may post more than one comment in a single thread. So it's quite complicated to get
  // backwards from the comments back to the reviews.
  for (const thread of threads) {
    const comments = thread.commentsData?.comments ?? []

    for (const comment of comments)
      if (comment.reviewId) {
        const review = summariesById.get(String(comment.reviewId))
        if (!review) continue

        const reviewThreads = threadsByReview.get(review) ?? new Map<Thread, Omit<ReviewThread, 'thread'>>()
        const reviewAlreadyOwnsThread = reviewThreads.has(thread)
        const relevantComments = reviewThreads.get(thread) ?? {relevantCommentIds: []}

        relevantComments.relevantCommentIds.push(comment.id)
        // After the first time we assign a thread, we need to ensure the thread anchors (HTML IDs) remain unique if assigned again
        if (!reviewAlreadyOwnsThread && assignedThreadIds.has(thread.id)) relevantComments.anchorPrefix = review.id

        assignedThreadIds.add(thread.id)
        reviewThreads.set(thread, relevantComments)
        threadsByReview.set(review, reviewThreads)
      }
  }

  const reviewsWithThreads = reviewSummaries.map(summary => ({
    summary,
    threads: Array.from(threadsByReview.get(summary) ?? []).map(([thread, rest]) => ({
      thread,
      ...rest,
    })),
  }))

  return {
    reviews: reviewsWithThreads,
    unassignedThreads: threads.filter(thread => !assignedThreadIds.has(thread.id)),
  }
}
